import $ from 'jquery';

import AlerterView from 'chairisher/view/alerter';
import CookieUtils from 'chairisher/util/cookie';
import ToggleView from 'chairisher/view/toggle';

import trackCookieSettingChange from 'chairisher/analytics/privacy';

import { getCookieSettingsUpdateUrl } from 'chairisher/context/account';
import { isAuthenticated } from 'chairisher/context/auth';
import {
    getAdvertisingCookiesCode,
    getCookieBannerCode,
    getCookiesPreferenceCode,
    getFunctionalCookiesCode,
    getOptOutOfCookiesValue,
    getPerformanceCookiesCode,
    isOptedOutOfCookie,
    setIsOptedOutOfCookie,
} from 'chairisher/context/cookies';
import { countryCodes, getPrimaryCountry, isUS } from 'chairisher/context/location';

const ChairishContext = window.chairisher.context;

class CookieBannerView {
    constructor() {
        /**
         * Flag indicating whether the user has made any changes to cookies and to show the alert for that
         *
         * @type {boolean}
         */
        this.isChanged = false;

        /**
         * @type {jQuery}
         */
        this.$cookieBanner = $('#js-cookie-banner');

        /**
         * @type {jQuery}
         */
        this.$cookieBannerModal = $('#js-cookie-banner-modal');

        /**
         * Flag indicating whether the user has made any changes to cookies and to show the alert for that
         *
         * @type {boolean}
         */
        this.isChanged = false;

        /**
         * Queue of settings dictionaries to make ajax calls with which update a user's cookie settings
         *
         * @type {array}
         */
        this.settingsToggleQueue = [];

        /**
         * @enum {string}
         */
        this.toggleIdToCookieMap = {
            'js-functional-cookies-toggle': getFunctionalCookiesCode(),
            'js-performance-cookies-toggle': getPerformanceCookiesCode(),
            'js-advertising-cookies-toggle': getAdvertisingCookiesCode(),
        };
        const cookieDismissedLocation = CookieUtils.getValueFromCookie(getCookieBannerCode());
        const isCookieBannerDismissed = !!cookieDismissedLocation;
        const isCookiePreferencesSet = !!CookieUtils.getValueFromCookie(getCookiesPreferenceCode());

        this.shouldShowBanner(isCookieBannerDismissed, isCookiePreferencesSet);

        // synch users who have moved domains
        // US users with temporary dismissal get permanent dismissal
        if (isCookieBannerDismissed && !isCookiePreferencesSet && isUS() && isAuthenticated()) {
            this.optOut(true);
            // NON-US users who dismissed while anonymous get the banner again
        } else if (!isUS() && cookieDismissedLocation === countryCodes.anonymous && isAuthenticated()) {
            this.unOptOut();
        }

        this.$cookieBanner.on('click', '.js-cookie-settings-btn', (e) => {
            e.preventDefault();
            this.showModal();
        });

        this.$cookieBannerModal.on('click', '.js-done-btn', () => {
            Object.keys(this.toggleIdToCookieMap).forEach((toggleIdToCookieMapKey) => {
                if ({}.hasOwnProperty.call(this.toggleIdToCookieMap, toggleIdToCookieMapKey)) {
                    const cookieCode = this.toggleIdToCookieMap[toggleIdToCookieMapKey];
                    const $btnToggle = $(`#${toggleIdToCookieMapKey}`);
                    const isOff = !$btnToggle.hasClass($btnToggle.data('active-class'));
                    const isOptedOut = isOptedOutOfCookie(cookieCode);
                    if (isOff !== isOptedOut) {
                        this.updateSetting(cookieCode, isOff);
                    }
                }
            });
            this.optOut(true);
            this.hideBanner();
            this.trigger('close');
            this.$cookieBannerModal.modal('hide');
        });

        const $btnToggles = this.$cookieBannerModal.find('.js-btn-toggle');
        $btnToggles.each((i) => {
            const $btnToggle = $btnToggles.eq(i);
            const toggleView = new ToggleView({
                $toggleEl: $btnToggle,
            });

            $btnToggle.on('click', (e) => {
                e.preventDefault();
                toggleView.toggle();
                const cookieCode = this.toggleIdToCookieMap[$btnToggle.attr('id')];
                const isOptedOut = !$btnToggle.hasClass($btnToggle.data('active-class'));
                this.updateSetting(cookieCode, isOptedOut);
            });
        });

        this.$cookieBanner.on('click', '.js-ok-btn', (e) => {
            e.preventDefault();
            this.hideBanner();
            this.optOut(false);
            this.trigger('close');
        });

        $('.js-manage-cookies-link').on('click', () => {
            this.showModal();
        });

        this.$cookieBannerModal.on('hidden.bs.modal', () => {
            if (this.isChanged) {
                AlerterView.success(ChairishContext.COOKIE_PREFERENCES_UPDATED_TEXT);
                this.isChanged = false;
            }
        });
    }

    /**
     * Determines whether banner should show
     *
     * @param {boolean} isCookieBannerDismissed
     * @param {boolean} isCookiePreferencesSet
     */
    shouldShowBanner(isCookieBannerDismissed, isCookiePreferencesSet) {
        if (
            !isCookieBannerDismissed && // session dismissal
            !isCookiePreferencesSet && // permanent dismissal
            !document.referrer.startsWith(document.location.origin) // Check if site is coming from chairish
        ) {
            this.showBanner();
        }
    }

    /**
     * @returns {jQuery}
     */
    getCookieBanner() {
        return this.$cookieBanner;
    }

    /**
     * Updates individual privacy setting
     *
     * @param {string} cookieCode
     * @param {boolean} isOptedOut
     */
    updateSetting(cookieCode, isOptedOut) {
        setIsOptedOutOfCookie(cookieCode, isOptedOut);
        this.isChanged = true;

        trackCookieSettingChange(cookieCode, isOptedOut);

        if (isAuthenticated()) {
            this.settingsToggleQueue.push({
                cookie: cookieCode,
                is_opted_out: isOptedOut,
            });
            if (this.settingsToggleQueue.length === 1) {
                this.submitSettingsToggleQueue();
            }
        }
    }

    /**
     * Hides the banner element and sets a cookie to opt the user out of seeing that
     */
    hideBanner() {
        this.toggleBanner(false);
    }

    /**
     * Show the banner element
     */
    showBanner() {
        this.toggleBanner(true);
    }

    /**
     * Toggle the banner element
     *
     * @param {boolean} shouldShow
     */
    toggleBanner(shouldShow) {
        this.$cookieBanner.toggleClass('hidden', !shouldShow);
    }

    /**
     * True indicates that the cookie banner is visible
     *
     * @returns {boolean}
     */
    isVisible() {
        return !this.$cookieBanner.hasClass('hidden');
    }

    /**
     * Show the modal element and set the cookie switches to on/off according to the current cookies
     */
    showModal() {
        const $btnToggles = this.$cookieBannerModal.find('.js-btn-toggle');
        $btnToggles.each((i) => {
            const $btnToggle = $btnToggles.eq(i);
            const isOptedOut =
                CookieUtils.getValueFromCookie(this.toggleIdToCookieMap[$btnToggle.attr('id')]) ===
                getOptOutOfCookiesValue();
            // setting is not specifically disabled and either preferences have been set or in US
            const isOff = !(!isOptedOut && (isOptedOutOfCookie(getCookiesPreferenceCode()) || isUS()));
            $btnToggle.toggleClass('btn-toggle-on', !isOff);
            $btnToggle.toggleClass('btn-toggle-off', isOff);
        });
        this.$cookieBannerModal.modal();
    }

    /**
     * Sets a session cookie that opts the user out of seeing the banner
     * Sets a permanent flag for US or if from modal
     *
     * @param {boolean} isPermanent
     */
    optOut(isPermanent = false) {
        const cookiesPreferenceCode = getCookiesPreferenceCode();
        CookieUtils.setCookie(getCookieBannerCode(), isAuthenticated() ? getPrimaryCountry() : countryCodes.anonymous);
        if (isPermanent || isUS()) {
            CookieUtils.setCookie(cookiesPreferenceCode, getOptOutOfCookiesValue(), 365);
            this.updateSettings(cookiesPreferenceCode, true);
        }
    }

    /**
     * Removes a permanent flag that opts the user out of seeing the banner
     */
    unOptOut() {
        const cookiesPreferenceCode = getCookiesPreferenceCode();
        const cookiesBannerCode = getCookieBannerCode();
        CookieUtils.removeCookie(cookiesPreferenceCode);
        CookieUtils.removeCookie(cookiesBannerCode);
        this.updateSettings(cookiesPreferenceCode, false, () => {
            this.showBanner();
        });
    }

    /**
     * Triggers a new event while passing along any other additional data provided
     *
     * @param {string} type The new event's type
     */
    trigger(type) {
        this.getCookieBanner().trigger($.Event(`cookiebanner:${type}`));
    }

    /**
     * Checks the settingsToggleQueue for a setting to call ajax with
     * and then shifts that setting out of the queue.
     * If there is another setting in the queue, recurse until the queue is empty.
     */
    submitSettingsToggleQueue() {
        const ajaxData = this.settingsToggleQueue[0];
        this.updateSettings(ajaxData.cookie, ajaxData.is_opted_out, () => {
            this.settingsToggleQueue.shift();
            if (this.settingsToggleQueue.length) {
                this.submitSettingsToggleQueue();
            }
        });
    }

    /**
     * Persist settings to DB.
     * @param {boolean} cookiesPreferenceCode
     * @param {boolean} isOptedOut
     * @param {Function} callback
     */
    updateSettings(cookiesPreferenceCode, isOptedOut, callback = () => {}) {
        if (isAuthenticated()) {
            $.post(
                getCookieSettingsUpdateUrl(),
                {
                    cookie: cookiesPreferenceCode,
                    is_opted_out: isOptedOut,
                },
                callback,
            );
            return;
        }
        callback();
    }
}

export default CookieBannerView;
